/* eslint-disable no-use-before-define */
import { COUNTRY_FOREIGN, COUNTRY_POSTOFFICE, COUNTRY_US } from 'constants/enums/locations';
import { LocationModel } from 'forms/shared/types';
import { includesAny } from 'helpers/string/string';
import { DeepSafePartial } from 'types/types';

/**
 * This function makes a determination on the value of 'country' based on other
 * values within the LocationModel.
 *
 * 1. United States === United States
 * 2. INTERNATIONAL === ''
 * 3. POSTOFFICE === POSTOFFICE
 */
const handleUnexplicitCountryValue = (location?: DeepSafePartial<LocationModel>): string => {
  if (!location) { return ''; }

  const { country } = location;
  const countryValue = getCountryValue(country, false);

  if (countryValue === COUNTRY_FOREIGN) { return ''; }

  if (countryValue === '') {
    if (includesAny(location.city ?? '', ['APO', 'FPO'])) { return COUNTRY_POSTOFFICE; }
    if (includesAny(location.state ?? '', ['AA', 'AE', 'AP'])) { return COUNTRY_POSTOFFICE; }

    // Unique to US
    if (location.county || location.state || location.zipcode) { return COUNTRY_US; }

    if (location.countryComments) { return ''; }

    return COUNTRY_US;
  }

  return countryValue;
};

const parseInternational = (countryString: string, explicit: boolean): string => (countryString === COUNTRY_FOREIGN && explicit ? '' : countryString);

export type HelperLocationParameters =
  | DeepSafePartial<LocationModel>
  | DeepSafePartial<LocationModel['country']>
export const getCountryValue = (
  location?: HelperLocationParameters,

  /**
   * 1. When explicit is set to true, this will do a strict comparison of the
   *    value. When set to false and the parameter is a LocationModel, it'll
   *    check the other values to make a determination on what country should
   *    be.
   *
   * 2. When explicit is set to true, it'll automatically replace INTERNATIONAL
   *    with ''. However, when set to false, it'll return INTERNATIONAL. This is
   *    meant to be used with other helper functions. We know when this value is
   *    set to INTERNATIONAL, it was doneso by a user.
   *
   * Default: true
   */
  explicit = true
): string => {
  if (isPartialLocationModel(location)) {
    if (!explicit) { return handleUnexplicitCountryValue(location); }

    return getCountryValue(location.country);
  }

  // handles string
  if (typeof location === 'string') { return parseInternational(location, explicit); }

  // handle object
  if (typeof location === 'object') {
    // handle countryArrayModel
    if (Array.isArray(location.value)) {
      if (location.value.length > 0) { return parseInternational(location?.value?.[0] ?? '', explicit); }

      // handle empty array
      return '';
    }

    if (typeof location.value === 'string') { return parseInternational(location.value, explicit); }
  }

  return '';
};

const isPartialLocationModel = (
  location?: DeepSafePartial<LocationModel> | DeepSafePartial<LocationModel['country']>
): location is DeepSafePartial<LocationModel> => {
  if (!!location && typeof location === 'object') {
    const keys = Object.keys(location);

    if (keys.includes('value')) { return false; }

    // Could be more explicit
    return true;
  }

  return false;
};

const COUNTRIES = {
  US: 'United States',
  INTERNATIONAL: '',
  POSTOFFICE: 'POSTOFFICE',
};

const isSafeCountryTypeWrapper = (type: 'US' | 'INTERNATIONAL' | 'POSTOFFICE') => (
  location?: HelperLocationParameters,

  /**
   * When explicit is set to true, this will do a strict comparison of the
   * value. When set to false and the parameter is a LocationModel, it'll check
   * the other values to make a determination on what country should be.
   *
   * Default: true
   */
  explicit = true
): boolean => {
  const value = COUNTRIES[type];

  if (value !== '') {
    if (isPartialLocationModel(location)) {
      if (!explicit) { return handleUnexplicitCountryValue(location) === value; }

      return getCountryValue(location?.country) === value;
    }

    return getCountryValue(location) === value;
  }

  return (
    !isSafeCountryTypeWrapper('US')(location, explicit)
    && !isSafeCountryTypeWrapper('POSTOFFICE')(location, explicit)
  );
};

export const isSafeUnitedStates = isSafeCountryTypeWrapper('US');
export const isSafePostOffice = isSafeCountryTypeWrapper('POSTOFFICE');
export const isSafeInternational = isSafeCountryTypeWrapper('INTERNATIONAL');

/**
 * This is meant to be a temporarily solution. A lot of location components do
 * "prop spreading" with other location components, which can create bugs, prop
 * pollution, and wasted re-renders. The data structure for a location model
 * needs to be rebuilt and passed into other helper functions, without passing
 * "props" into every function.
 */
export const createLocationModelObjectFromProps = (props: any): Partial<LocationModel> => ({
  street: props?.street,
  street2: props?.street2,
  city: props?.city,
  county: props?.county,
  zipcode: props?.zipcode,
  state: props?.state,
  country: props?.country,
});

export const internalTestingHelperLocations = {
  parseInternational,
  handleUnexplicitCountryValue,
  isSafeCountryTypeWrapper,
};
